<template>
  <AlertDialog :open="showDialog">
    <AlertDialogContent class="tw:sm:max-w-3xl tw:max-h-[80vh] tw:flex tw:flex-col">
      <AlertDialogHeader>
        <AlertDialogTitle>{{ $t('Terms of use') }}</AlertDialogTitle>
        <AlertDialogDescription class="tw:sr-only">
          {{ $t('You must accept the terms of use to continue.') }}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div class="tw:flex-1 tw:overflow-y-auto tw:my-4">
        <div class="tw:prose tw:max-w-none tw:prose-sm" v-html="termsContent"></div>
        <p class="tw:text-muted-foreground tw:mt-4 tw:text-sm" v-if="publishedDate">
          {{ $t("Published on {date}", { date: publishedDate }) }}
        </p>
      </div>

      <form ref="termsForm" @submit.prevent="handleAccept" method="POST" :action="termAcceptanceUrl">
        <input type="hidden" name="_token" :value="csrfToken" />

        <AlertDialogFooter class="tw:flex-col tw:sm:flex-col tw:gap-3">
          <div class="tw:w-full">
            <label class="tw:flex tw:items-center tw:gap-2 tw:cursor-pointer">
              <input 
                type="checkbox" 
                name="accept_terms"
                v-model="acceptTerms" 
                class="tw:rounded tw:border-gray-300 tw:text-primary tw:focus:ring-primary"
                :disabled="submitting"
              />
              <span class="tw:text-sm">{{ $t('I accept the terms of use') }}</span>
            </label>
            <div v-if="validationError" class="tw:text-red-600 tw:text-sm tw:mt-1">
              {{ validationError }}
            </div>
          </div>
          
          <AlertDialogAction
            type="submit"
            :disabled="!acceptTerms || submitting"
            class="tw:w-full"
          >
            {{ submitting ? $t('Saving...') : $t('Save') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </form>
    </AlertDialogContent>
  </AlertDialog>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from "@/shadcn/components/ui/alert-dialog";

const { t } = useI18n()

interface Props {
  termsContent?: string
  termAcceptanceUrl: string,
  publishedDate?: string
  showDialog?: boolean
}

withDefaults(defineProps<Props>(), {
  termsContent: '',
  publishedDate: '',
  showDialog: false,
})

const termsForm = ref<HTMLFormElement | null>(null)
const acceptTerms = ref(false)
const submitting = ref(false)
const validationError = ref<string | null>(null)
const csrfToken = ref('')

onMounted(() => {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  if (!token) {
    validationError.value = t('Security token not found. Please refresh the page.')
  }
  csrfToken.value = token || ''
})

const handleAccept = () => {
  if (!acceptTerms.value) {
    validationError.value = t('You must accept the terms of use to continue.')
    return
  }

  if (!csrfToken.value) {
    validationError.value = t('Security token not found. Please refresh the page.')
    return
  }

  submitting.value = true
  validationError.value = null

  // Submit the form
  if (termsForm.value) {
    termsForm.value.submit()
  }
}
</script>
